import type { FunctionComponent } from 'react';

import { API_WEBHOOK_NOTIFICATIONS } from '../../../../shared/featureFlags/flagNames';
import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import Flex from '../../generic/Flex';
import PaidPill from '../../generic/Pill/PaidPill';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import SettingsPage from '../../shared/settings/SettingsPage';
import * as permissions from '../shared/permissions';
import type { RouterProps } from '../../../types';

type Props = RouterProps;

/**
 * OrganizationIntegrationSettings is the top-level wrapper component for
 * all the integration settings pages.
 */
const OrganizationIntegrationSettings: FunctionComponent<Props> = ({ children, location }) => {
  const canManageServiceAccounts = permissions.useCanManageServiceAccounts();
  const canViewOIDCProviders = permissions.useCanViewOIDCProviders();
  const isApiWebhookNotificationsEnabled = useIsFeatureEnabled(API_WEBHOOK_NOTIFICATIONS);
  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav pathname={location.pathname}>
        <SidebarNavLayout.NavHeading>
          Integrations
        </SidebarNavLayout.NavHeading>
        {permissions.shouldDisplayJiraSettings() && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/jira" activeKey="jira">
            <Flex justify="space-between">
              Jira
              {isOrgFreeTier && <PaidPill />}
            </Flex>
          </SidebarNavLayout.NavItem>
        )}
        <SidebarNavLayout.NavItem to="/account/settings/integrations/github" activeKey="github">
          GitHub
        </SidebarNavLayout.NavItem>
        {permissions.shouldDisplaySlackSettings() && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/slack" activeKey="slack">
            <Flex justify="space-between">
              Slack
              {isOrgFreeTier && <PaidPill />}
            </Flex>
          </SidebarNavLayout.NavItem>
        )}
        <SidebarNavLayout.NavItem to="/account/settings/integrations/bitbucket" activeKey="bitbucket">
          Bitbucket
        </SidebarNavLayout.NavItem>
        <SidebarNavLayout.NavItem to="/account/settings/integrations/gitlab" activeKey="gitlab">
          GitLab
        </SidebarNavLayout.NavItem>
        <SidebarNavLayout.NavItem to="/account/settings/integrations/api_tokens" activeKey="api_tokens">
          API
        </SidebarNavLayout.NavItem>
        {canManageServiceAccounts && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/api_user" activeKey="api_user">
            API Service Accounts
          </SidebarNavLayout.NavItem>
        )}
        {canViewOIDCProviders && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/oidc" activeKey="oidc">
            <Flex justify="space-between">
              OIDC Providers
              {isOrgFreeTier && <PaidPill />}
            </Flex>
          </SidebarNavLayout.NavItem>
        )}
        {permissions.shouldDisplayCustomLicenseScanSettings() && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/custom_license_scans" activeKey="custom_license_scans">
            Custom License Scans
          </SidebarNavLayout.NavItem>
        )}
        {isApiWebhookNotificationsEnabled && (
          <SidebarNavLayout.NavItem to="/account/settings/integrations/api_webhooks" activeKey="api_webhooks">
            API Webhooks
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        <SettingsPage>
          {children}
        </SettingsPage>
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default OrganizationIntegrationSettings;
